/*
Pedido de copia de llave (AF298): modelo del pedido y del token del link.
Lo comparten el formulario público (pages/key-request-form) y la pantalla de la administración (subtarea 2).
*/

// pedidos: subcolección de cada consorcio (como los reclamos), con administrationUserId para buscarlos con collectionGroup
export const KEY_REQUESTS_SUBCOLLECTION = 'keyRequests';
// tokens de los links: los crea la function createformlink (type keyRequest)
export const KEY_REQUEST_TOKENS = 'keyRequestTokens';

export type KeyRequestRecipientType = 'self' | 'registered' | 'unregistered';
export type KeyRequestKeyType = 'electronic' | 'other';
export type KeyRequestReason = 'newPerson' | 'extraCopy' | 'replacement' | 'other';
export type KeyRequestRelation = 'family' | 'partner' | 'tenant' | 'owner' | 'employee' | 'other';
export type KeyRequestStatus = 'pending' | 'accepted' | 'rejected';
export type KeyRequestOccupancyRole = 'owner' | 'tenant' | 'realEstate' | 'other';
export type KeyRequestTokenStatus = 'active' | 'used' | 'invalidated';

export const KEY_REQUEST_RELATION_OPTIONS: { value: KeyRequestRelation, label: string }[] = [
    { value: 'family', label: 'Familiar' },
    { value: 'partner', label: 'Pareja' },
    { value: 'tenant', label: 'Inquilino/a' },
    { value: 'owner', label: 'Propietario/a' },
    { value: 'employee', label: 'Empleado/a' },
    { value: 'other', label: 'Otro' },
];

export const KEY_REQUEST_KEY_TYPE_OPTIONS: { value: KeyRequestKeyType, label: string }[] = [
    { value: 'electronic', label: 'Electrónica' },
    { value: 'other', label: 'Otro' },
];

export const KEY_REQUEST_REASON_OPTIONS: { value: KeyRequestReason, label: string }[] = [
    { value: 'newPerson', label: 'Persona nueva' },
    { value: 'extraCopy', label: 'Copia adicional' },
    { value: 'replacement', label: 'Reemplazo' },
    { value: 'other', label: 'Otro' },
];

export interface KeyRequestPhone {
    countryCode: string;
    phoneArea: string;
    phoneNumber: string;
}

export interface KeyRequestConsorcistaRef {
    consorcioId: string;
    consorcistaId: string;
}

/* keyRequestTokens/{cod} (ver la function createformlink, form-links/key-request.ts) */
export interface KeyRequestToken {
    cod: string;
    administrationId: string;
    wspId: string;
    // unidades de la persona en esta administración al crear el link
    consorcistas: KeyRequestConsorcistaRef[];
    // unidad que mandó el agente (el formulario no la pregunta)
    preselected?: KeyRequestConsorcistaRef;
    status: KeyRequestTokenStatus;
    createdAt: Date;
    expiresAt: Date;
    usedAt?: Date;
    invalidatedAt?: Date;
}

/* consorcios/{consorcioId}/keyRequests/{uid} */
export interface KeyRequest {
    uid?: string;
    // identificador de envío: el cod del link (para agrupar pedidos más adelante)
    envioId: string;
    canal: 'Whatsapp';
    // WhatsApp del solicitante (para el chat y los mensajes)
    wspId: string;
    administrationUserId: string;
    consorcioId: string;
    consorcioName: string;
    unit: { tower?: string; floor?: string; apartment?: string; label: string };
    requester: {
        consorcistaId: string;
        firstName: string;
        lastName?: string;
        occupancyRole?: KeyRequestOccupancyRole;
    };
    recipient: {
        type: KeyRequestRecipientType;
        consorcistaId?: string;
        firstName: string;
        lastName?: string;
        dni: string;
        phone?: KeyRequestPhone;
        relation?: KeyRequestRelation;
        relationOther?: string;
    };
    // solo si quien pide es una inmobiliaria
    pickupResponsibleDni?: string;
    keyType: KeyRequestKeyType;
    keyTypeOther?: string;
    reason: KeyRequestReason;
    lostKey: boolean;
    status: KeyRequestStatus;
    rejectionReason?: string;
    createdAt: Date;
    resolvedAt?: Date;
}

// para la tabla y el detalle de la administración (app-progress-status: step y variant como en reclamos)
export const KEY_REQUEST_STATUS_STEPS: Record<KeyRequestStatus, { label: string, step: number, variant: 'waiting' | 'success' | 'error' | 'action' | 'working' }> = {
    //espera al administrador (AF340): "Por atender", azul
    pending: { label: 'Por atender', step: 1, variant: 'action' },
    accepted: { label: 'Aceptado', step: 1, variant: 'success' },
    rejected: { label: 'Rechazado', step: 1, variant: 'error' },
};

export const KEY_REQUEST_ROLE_LABELS: Record<KeyRequestOccupancyRole, string> = {
    owner: 'Propietario/a',
    tenant: 'Inquilino/a',
    realEstate: 'Inmobiliaria',
    other: 'Otro',
};

export const KEY_REQUEST_RECIPIENT_LABELS: Record<KeyRequestRecipientType, string> = {
    self: 'El mismo solicitante',
    registered: 'Persona registrada en la unidad',
    unregistered: 'Persona no registrada',
};

export function optionLabel<T extends string>(options: { value: T, label: string }[], value: T | undefined): string {
    return options.find(o => o.value === value)?.label ?? (value ?? '');
}

export function formatKeyRequestPhone(phone?: KeyRequestPhone): string {
    if (!phone || (!phone.phoneArea && !phone.phoneNumber)) return '';
    return `+${phone.countryCode || '54'} (${phone.phoneArea}) ${phone.phoneNumber}`;
}

// edificio y unidad para mostrar y copiar: "Prueba Test 1 - Piso 9 - Depto Z"
export function keyRequestUnitText(request: Pick<KeyRequest, 'consorcioName' | 'unit'>): string {
    return [request.consorcioName, request.unit?.label].filter(Boolean).join(' - ');
}

// texto para la empresa de llaves: solo lo que pide, en este orden (consigna AF298)
export function buildKeyRequestCopyText(request: KeyRequest): string {
    const r = request.recipient;
    return [
        [r.firstName, r.lastName].filter(Boolean).join(' '),
        'DNI ' + r.dni,
        formatKeyRequestPhone(r.phone),
        keyRequestUnitText(request),
    ].filter(Boolean).join('\n');
}

// DNI: solo números, 7 u 8 dígitos (se sacan puntos, espacios y guiones antes de validar)
export function normalizeDni(value: string | null | undefined): string {
    return String(value ?? '').replace(/[.\s-]/g, '');
}

export function isValidDni(value: string | null | undefined): boolean {
    return /^\d{7,8}$/.test(normalizeDni(value));
}
